import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { HeroIcons } from '../../../lib/utils/heroIcons'
import PropertyService from '../../../services/property.service'
import { useTranslation } from 'next-i18next'
import { NextSeo } from 'next-seo'
import FsLightbox from 'fslightbox-react'
import ConfettiExplosion from 'react-confetti-explosion'
import {
  PropertyNew,
  PropertyWithAccess,
} from '../../../types/Property/Property'
import { classNames } from '../../../lib/utils/classNames'
import InfoSection from '../../../components/originals/property/InfoSection/InfoSection'
import ProjectSection from '../../../components/originals/property/ProjectSection/ProjectSection'
import DpeWorkSection from '../../../components/originals/property/DpeWorkSection/DpeWorkSection'
import BuyCard from '../../../components/originals/property/BuyingSection/BuyCard'
import FinancingAdvancementCard from '../../../components/originals/property/BuyingSection/FinancingAdvancementCard'
import FinancingCountdownCard from '../../../components/originals/property/BuyingSection/FinancingCountdownCard'
import ImagesSection from '../../../components/originals/property/ImagesSection/ImagesSection'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import DocumentSection from '../../../components/originals/property/DocumentsSection/DocumentSection'
import BlockchainSection from '../../../components/originals/property/BlockchainSection/BlockchainSection'
import { setCurrentProperty, useDispatch } from '../../../store'
import UserTestimonialCard from '../../../components/originals/property/BuyingSection/UserTestimonialCard'
import { GoogleTagManagerService } from '../../../services/google-tag-manager.service'
import { mapContent, mapItem } from '../../../lib/utils/helperGtm'
import { isMobileDevice } from '../../../lib/isMobileDevice'
import Layout from '../../../components/Layout'
import MobileBuyCard from '../../../components/originals/property/BuyingSection/MobileBuyCard'
import InvestmentSimulationBento from '../../../components/home/bento/InvestmentSimulationBento'
import useLocale from '../../../hooks/useLocale'
import propertyService from '../../../services/property.service'
import Link from 'next/link'
import routes from '../../../config/routes'
import RequestInvitationForm from '../../../components/club/RequestInvitationForm'
import ClubAccessVisual from '../../../components/club/ClubAccessVisual'
import clubService from '../../../services/club.service'
import { ClubEntry, ClubEntryStatus } from '../../../types/club/ClubEntry'
import ContactUsBento from '../../../components/home/bento/ContactUsBento'
import { hasPromo } from '../../../lib/utils/promo'
import { useAuth } from '../../../providers/AuthProvider'
import { useRouter } from 'next/router'
import removeQueryParams from '../../../lib/utils/removeQueryParams'

interface tab {
  name: string
  current: boolean
  icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
      title?: string | undefined
      titleId?: string | undefined
    } & React.RefAttributes<SVGSVGElement>
  >
  content: React.JSX.Element
}

function PropertyPage({
  property,
  isMobile,
}: {
  property: PropertyWithAccess
  isMobile: boolean
}) {
  const { t } = useTranslation('originals/property')
  const { t: t2 } = useTranslation('club')
  const { locale } = useLocale()
  const { isLoggedIn, isLoading } = useAuth()
  const router = useRouter()
  const { clubAccepted } = router.query

  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 1,
  })
  const dispatch = useDispatch()
  const [tabs, setTabs] = useState<tab[]>([])
  const currentTab = tabs.find((tab) => tab.current)
  const [propertyNews, setPropertyNews] = useState<PropertyNew[]>([])
  const [clubEntry, setClubEntry] = useState<ClubEntry>()
  const [minFractionAmount, setMinFractionAmount] = useState<number>(1)
  const isPromo = hasPromo(property.nftCollectionId)
  const [showClubAcceptedCelebration, setShowClubAcceptedCelebration] =
    useState<boolean>(false)
  const [
    isClubAcceptedCelebrationVisible,
    setIsClubAcceptedCelebrationVisible,
  ] = useState<boolean>(false)
  const clubAcceptedTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const clubAcceptedCelebrationKeyRef = useRef<string | null>(null)

  const fetchPropertyNews = async () => {
    const _propertyNews = await propertyService.getNews(property.id)
    setPropertyNews(_propertyNews)
  }

  const fetchClubEntry = async () => {
    if (!property || !property.clubId) return
    if (!isLoggedIn || isLoading) return

    const _clubEntry = await clubService.getMeClubEntry(property.clubId)
    if (_clubEntry) {
      const _minFractionAmount =
        _clubEntry.status === ClubEntryStatus.COMPLETED
          ? 1
          : _clubEntry.amount
            ? Math.floor(_clubEntry.amount / property.fractionInitialPrice)
            : 1
      setMinFractionAmount(_minFractionAmount)
      setClubEntry(_clubEntry)
    }
  }

  const setTabsContent = () => {
    if (!property || !property.allowed) return

    const isDpeTab: boolean =
      property.gazEmission > 0 || property.energyConsumption > 0

    const tabs = [
      {
        name: t('navbar.property'),
        icon: HeroIcons.HomeIcon,
        current: true,
        content: (
          <InfoSection property={property} propertyNews={propertyNews} />
        ),
      },
      {
        name: t('navbar.project'),
        icon: HeroIcons.RectangleGroupIcon,
        current: false,
        content: <ProjectSection property={property} />,
      },
      {
        name: t('navbar.documents'),
        icon: HeroIcons.ArchiveBoxIcon,
        current: false,
        content: <DocumentSection property={property} />,
      },
      {
        name: t('navbar.blockchain'),
        icon: HeroIcons.NewspaperIcon,
        current: false,
        content: <BlockchainSection property={property} />,
      },
    ]

    if (isDpeTab) {
      tabs.splice(2, 0, {
        name: t('navbar.dpeAndWork'),
        icon: HeroIcons.WrenchScrewdriverIcon,
        current: false,
        content: <DpeWorkSection property={property} />,
      })
    }

    setTabs(tabs)
  }

  const handleTabClick = (clickedTabIndex: number) => {
    const updatedTabs = tabs.map((tab, index) => ({
      ...tab,
      current: index === clickedTabIndex,
    }))
    setTabs(updatedTabs)
  }

  const openLightboxOnSlide = (index: number) => {
    setLightboxController({
      toggler: !lightboxController.toggler,
      slide: index,
    })
  }

  useEffect(() => {
    fetchClubEntry()
  }, [property, isLoading])

  useEffect(() => {
    fetchPropertyNews()
  }, [locale, property.id])

  useEffect(() => {
    dispatch(setCurrentProperty(property))
    GoogleTagManagerService.ViewItem({
      item: mapItem(property),
      content: mapContent(property),
    })
  }, [property])

  useEffect(() => {
    setTabsContent()
  }, [t, property, propertyNews])

  useEffect(() => {
    if (!router.isReady || !clubAccepted) return

    const celebrationKey = `${property.id}-${String(clubAccepted)}`
    if (clubAcceptedCelebrationKeyRef.current === celebrationKey) return

    clubAcceptedCelebrationKeyRef.current = celebrationKey
    clubAcceptedTimersRef.current.forEach((timer) => clearTimeout(timer))
    clubAcceptedTimersRef.current = []

    setShowClubAcceptedCelebration(true)
    setIsClubAcceptedCelebrationVisible(false)

    const showTimer = setTimeout(
      () => setIsClubAcceptedCelebrationVisible(true),
      80,
    )
    const hideTimer = setTimeout(
      () => setIsClubAcceptedCelebrationVisible(false),
      5300,
    )
    const unmountTimer = setTimeout(
      () => setShowClubAcceptedCelebration(false),
      6200,
    )
    clubAcceptedTimersRef.current = [showTimer, hideTimer, unmountTimer]

    removeQueryParams(router, ['clubAccepted'])
  }, [clubAccepted, property.id, router, router.isReady])

  useEffect(() => {
    return () => {
      clubAcceptedTimersRef.current.forEach((timer) => clearTimeout(timer))
      clubAcceptedTimersRef.current = []
    }
  }, [])

  const clubAcceptedCelebrationOverlay = showClubAcceptedCelebration && (
    <div
      className={`pointer-events-none fixed inset-0 z-[120] flex items-center justify-center p-3 transition-opacity duration-500 sm:p-4 ${
        isClubAcceptedCelebrationVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/85 via-white/70 to-amber-50/70 backdrop-blur-[2px]" />
      <div className="absolute left-3 top-6 origin-top-left scale-75 sm:left-8 sm:top-8 sm:scale-100">
        <ConfettiExplosion />
      </div>
      <div className="absolute right-3 top-6 origin-top-right scale-75 sm:right-8 sm:top-8 sm:scale-100">
        <ConfettiExplosion />
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 scale-75 sm:bottom-10 sm:scale-100">
        <ConfettiExplosion />
      </div>
      <div className="relative w-full max-w-[95vw] sm:max-w-xl">
        <div
          className={`relative overflow-hidden rounded-2xl border border-emerald-200/80 bg-white px-4 py-5 text-center shadow-[0_30px_80px_-30px_rgba(16,185,129,0.55)] transition-all duration-500 sm:rounded-[28px] sm:px-8 sm:py-7 ${
            isClubAcceptedCelebrationVisible
              ? 'translate-y-0 scale-[0.93] opacity-100 sm:scale-100'
              : 'translate-y-4 scale-[0.88] opacity-0 sm:scale-[0.98]'
          }`}
        >
          <div className="pointer-events-none absolute -left-16 -top-16 h-28 w-28 rounded-full bg-emerald-100/80 blur-2xl sm:-left-20 sm:-top-20 sm:h-40 sm:w-40" />
          <div className="pointer-events-none absolute -bottom-12 -right-10 h-24 w-24 rounded-full bg-amber-100/80 blur-2xl sm:-bottom-16 sm:-right-14 sm:h-36 sm:w-36" />
          <div className="relative flex flex-col items-center">
            <div className="flex w-fit items-center">
              <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-100 to-amber-100 text-emerald-700 sm:h-14 sm:w-14 sm:rounded-2xl">
                <HeroIcons.CheckBadgeIcon className="h-6 w-6 sm:h-8 sm:w-8" />
              </div>
              <p className="inline-flex px-2.5 py-1 text-sm font-semibold uppercase tracking-wide text-emerald-700 sm:px-3 sm:text-xl sm:font-medium">
                {t('clubAccessCelebration.badge')}
              </p>
            </div>
            <h2 className="mt-3 text-xl font-semibold text-neutralBlack sm:text-2xl">
              {t('clubAccessCelebration.title')}
            </h2>
            <p className="mt-2 text-xs text-slate-600 sm:text-sm">
              {t('clubAccessCelebration.subtitle', {
                property: property.translation.title,
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  if (!property.allowed) {
    return (
      <div className="relative">
        {clubAcceptedCelebrationOverlay}
        <div className="flex grow flex-col justify-start">
          <div className="mb-4 flex w-full items-start">
            <Link href={routes.properties}>
              <div className="flex items-center rounded-full px-1 py-2 text-sm font-bold text-neutralBlack transition hover:text-brandLightBlue">
                <HeroIcons.ArrowLeftIcon className="mr-2 h-5 w-5" />
                {t2('requestInvitation.backToListing')}
              </div>
            </Link>
          </div>
          <div className="w-full overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-7">
                <RequestInvitationForm
                  clubId={property.clubId as string}
                  propertyId={property.id}
                />
              </div>

              <div className="h-full md:col-span-5">
                <ClubAccessVisual t={t2} />
              </div>
            </div>
          </div>

          <div className="mt-3 w-full overflow-hidden rounded-md bg-white p-3 shadow-sm">
            <ContactUsBento />
          </div>
        </div>
      </div>
    )
  }

  if (!property) {
    return <></>
  }

  const isReimbursed = property.fundingStatus === 'reimbursed'

  return (
    <div className="relative">
      {clubAcceptedCelebrationOverlay}

      <div className="relative mb-28 flex flex-col sm:mb-0">
        <NextSeo
          title={t('seo.title', { title: property.translation.title })}
          description={
            property.translation.descriptions?.projectDetails?.description?.substring(
              0,
              200,
            ) || ''
          }
        />
        <FsLightbox
          type={'image'}
          toggler={lightboxController.toggler}
          slide={lightboxController.slide}
          sources={property.images.map((image) => image.url)}
          exitFullscreenOnClose={true}
        />

        <div className="lg:flex lg:space-x-6 lg:pt-0">
          <div className="w-full lg:w-[70%]">
            <ImagesSection
              images={property.images}
              onImageClick={openLightboxOnSlide}
              isMobile={isMobile}
            />
            <div className="relative top-0 z-10 bg-slate-100 pt-3 lg:sticky">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-3xl font-medium text-neutralBlack">
                    {property.translation.title}
                  </h1>
                </div>
                <div className="flex items-center">
                  <HeroIcons.MapPinIcon className="mr-2 h-4 w-auto text-neutralBlack" />
                  <div className="font-thin text-neutralBlack">
                    {property.addressStreet} - {property.addressCity}
                  </div>
                </div>
              </div>
              {isPromo ? (
                <div className="mt-3 overflow-hidden rounded-2xl border border-amber-200/70 bg-gradient-to-r from-amber-50 via-white to-amber-50 px-4 py-3 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-amber-100 p-2 text-amber-800">
                        <HeroIcons.ReceiptPercentIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                            {t('promo.badge')}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-neutralBlack">
                          {t('promo.title')}
                        </p>
                        <p className="text-xs text-gray-600">
                          {t('promo.subtitle')}
                        </p>
                      </div>
                    </div>
                    <div className="hidden text-xs font-semibold text-amber-800 md:block">
                      {t('promo.cta')}
                    </div>
                  </div>
                </div>
              ) : null}
              {property.fundingStatus === 'reimbursed' ? (
                <div className="mt-3 overflow-hidden rounded-lg border border-emerald-200/80 bg-white px-4 py-3 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="rounded-md bg-emerald-100 p-2 text-emerald-700">
                        <HeroIcons.ShieldCheckIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </div>
                      <div>
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                          {t('reimbursedBanner.badge')}
                        </span>
                        <p className="text-sm font-semibold text-neutralBlack">
                          {t('reimbursedBanner.title')}
                        </p>
                        <p className="text-xs text-gray-600">
                          {t('reimbursedBanner.subtitle')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="pt-3">
                <nav
                  className={`grid w-full grid-cols-${
                    tabs.length > 0 ? tabs.length : '5'
                  } border-b border-neutralGray`}
                  aria-label="Tabs"
                >
                  {tabs.length > 0 &&
                    tabs.map((tab, index) => (
                      <button
                        key={tab.name}
                        onClick={() => handleTabClick(index)}
                        className={classNames(
                          tab.current
                            ? 'border-brandLightBlue font-bold text-brandLightBlue'
                            : 'border-transparent text-neutralBlack hover:text-brandLightestBlue',
                          'group flex cursor-pointer select-none items-center border-b-2 pb-2 text-center text-xs md:text-sm',
                        )}
                      >
                        <div className="mx-auto flex items-center">
                          <tab.icon
                            className="ml-0.5 mr-2 hidden h-3 w-auto lg:block lg:h-5"
                            aria-hidden="true"
                          />
                          <span>{tab.name}</span>
                        </div>
                      </button>
                    ))}
                </nav>
              </div>
            </div>

            <div className="scroll-m-7 pt-3">
              {currentTab && currentTab.content}
              <div className="block pb-3 pt-6 lg:hidden">
                <UserTestimonialCard
                  fileName={'stephane-madryga'}
                  title={'Stéphane Madryga'}
                  subtitle={'Porteur de projet pour Shelters'}
                />
              </div>
            </div>
          </div>

          <div className="sm:block lg:hidden">
            <div className="overflow-hidden rounded-md bg-white p-3">
              <InvestmentSimulationBento
                locked
                defaultYearlyYield={property.rentalYieldPerYear}
                defaultYearlyPriceAppreciation={property.revaluationPerYear}
                defaultTotalPeriod={1}
              />
            </div>
          </div>

          <div className="hidden flex-1 lg:block">
            <div className="space-y-4 md:top-4 lg:sticky">
              <div id="buy-card-desktop">
                {isReimbursed ? (
                  <div className="rounded-md border border-emerald-200/80 bg-white p-4">
                    <div className="flex items-start gap-3">
                      <div className="rounded-md bg-emerald-100 p-2 text-emerald-700">
                        <HeroIcons.ShieldCheckIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-neutralBlack">
                          {t('reimbursedCard.title')}
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          {t('reimbursedCard.subtitle')}
                        </div>
                      </div>
                    </div>
                    <Link
                      href={routes.properties}
                      className="mt-3 inline-flex w-full items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                    >
                      {t('reimbursedCard.cta')}
                    </Link>
                  </div>
                ) : (
                  <BuyCard
                    property={property}
                    minFractionAmount={minFractionAmount}
                    clubEntry={clubEntry}
                  />
                )}
              </div>
              {['funding', 'funded', 'reimbursed'].includes(
                property.fundingStatus,
              ) && <FinancingAdvancementCard property={property} />}

              {property.fundingStatus === 'not_started' && (
                <FinancingCountdownCard
                  property={property}
                  onCountdownComplete={() => {}}
                />
              )}
              <div className="overflow-hidden rounded-md bg-white p-3">
                <InvestmentSimulationBento
                  locked
                  defaultYearlyYield={property.rentalYieldPerYear}
                  defaultYearlyPriceAppreciation={property.revaluationPerYear}
                  defaultTotalPeriod={1}
                />
              </div>
              <UserTestimonialCard
                fileName={'stephane-madryga'}
                title={'Stéphane Madryga'}
                subtitle={'Porteur de projet pour Shelters'}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        id="buy-card-mobile"
        className="fixed bottom-0 left-0 z-30 w-full sm:hidden"
      >
        {isReimbursed ? (
          <div className="w-full border-t border-emerald-200 bg-white px-3 py-3 shadow-[0_-6px_18px_rgba(15,23,42,0.08)]">
            <div className="mx-auto max-w-xl">
              <div className="flex items-start gap-2">
                <div className="rounded-md bg-emerald-100 p-1.5 text-emerald-700">
                  <HeroIcons.ShieldCheckIcon
                    className="h-4 w-4"
                    aria-hidden="true"
                  />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-neutralBlack">
                    {t('reimbursedCard.title')}
                  </div>
                  <div className="text-xs text-gray-600">
                    {t('reimbursedCard.subtitle')}
                  </div>
                </div>
              </div>
              <Link
                href={routes.properties}
                className="mt-2 inline-flex w-full items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700"
              >
                {t('reimbursedCard.cta')}
              </Link>
            </div>
          </div>
        ) : (
          <MobileBuyCard
            minFractionAmount={minFractionAmount}
            clubEntry={clubEntry}
            property={property}
          />
        )}
      </div>
    </div>
  )
}

PropertyPage.getLayout = (page: ReactElement) => {
  return <Layout>{page}</Layout>
}

export default PropertyPage

export async function getServerSideProps({
  locale,
  params,
  req,
}: {
  locale: string
  params: any
  req: any
}) {
  const { propertyId } = params

  const property = await PropertyService.getProperty({
    id: propertyId as string,
    lang: locale,
    headers: req.headers,
  })
  const isMobile = isMobileDevice(req)

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'home',
        'club',
        'originals/property',
        'originals/global',
      ])),
      property,
      isMobile,
    },
  }
}
